import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { toast } from "@/lib/toast";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Loader2, Lock, Bell, BellOff } from "lucide-react";
import { resolveForumTheme, buildForumPalette, buildForumBg } from "@/lib/forumThemes";
import { canModerateForum, canParticipateInForum } from "@/lib/accountRole";
import { ForumPost, ReplyBox, ReportModal } from "@/components/forum/ForumPost";
import { ModToolbar, RestrictDialog } from "@/components/forum/ForumModTools";
import { ShareLinkButton } from "@/components/ShareLinkButton";

export default function ForumThread() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useMemo(() => resolveForumTheme(user), [user]);
  const P = useMemo(() => buildForumPalette(theme), [theme]);
  const forumBg = useMemo(() => buildForumBg(theme), [theme]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [activeReply, setActiveReply] = useState(null); // null | 'op' | replyId
  const [replyText, setReplyText] = useState("");
  const [replyImages, setReplyImages] = useState([]);
  const [replyVideo, setReplyVideo] = useState(null);
  const [replyBusy, setReplyBusy] = useState(false);
  const [reacting, setReacting] = useState(null);
  const [reportTarget, setReportTarget] = useState(null); // {type,id} | null
  const [restrictOpen, setRestrictOpen] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [subBusy, setSubBusy] = useState(false);

  const canMod = canModerateForum(user);
  const canParticipate = canParticipateInForum(user);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/forum/threads/${id}`);
      let merged = d;
      if (user) {
        try {
          const { data: mr } = await api.get(`/forum/threads/${id}/my-reactions`);
          const map = mr.reactions || {};
          merged = {
            ...d,
            thread: { ...d.thread, my_reaction: map[id] || null },
            replies: (d.replies || []).map((r) => ({ ...r, my_reaction: map[r.reply_id] || null })),
          };
        } catch { /* keep counts without my_reaction */ }
      }
      setData(merged);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, [id, user]);
  useEffect(() => { load(); }, [load]);

  // Watch/subscription state (drives the Watch toggle + watched-thread reply emails).
  useEffect(() => {
    if (!user || !id) { setSubscribed(false); return; }
    let alive = true;
    api.get(`/forum/threads/${id}/subscription`)
      .then(({ data: d }) => { if (alive) setSubscribed(!!d.subscribed); })
      .catch(() => {});
    return () => { alive = false; };
  }, [user, id]);

  // Deep-link from a mention / watched-reply email (…/forum/thread/<id>#reply-<rid>) → scroll to it.
  useEffect(() => {
    if (loading || !data) return;
    const hash = window.location.hash;
    if (hash && hash.startsWith("#reply-")) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.style.transition = "box-shadow 0.3s";
        el.style.boxShadow = "0 0 0 2px var(--ring, #cfe86b)";
        setTimeout(() => { el.style.boxShadow = "none"; }, 2200);
      }
    }
  }, [loading, data]);

  const toggleWatch = async () => {
    if (!user) { toast("Sign in to watch this thread."); return; }
    setSubBusy(true);
    try {
      const next = !subscribed;
      const { data: d } = await api.post(`/forum/threads/${id}/subscribe`, { subscribe: next });
      setSubscribed(!!d.subscribed);
      toast.success(next ? "Watching — we'll email you on new replies." : "Stopped watching this thread.");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSubBusy(false); }
  };

  // Categories power the moderator "Move" control — only loaded for mods/admins.
  useEffect(() => {
    if (!canMod) return;
    api.get("/forum/categories").then(({ data: d }) => setCategories(d.categories || [])).catch(() => {});
  }, [canMod]);

  const applyCounts = (type, targetId, res) => {
    setData((prev) => {
      if (!prev) return prev;
      const patch = { likes: res.likes, dislikes: res.dislikes, my_reaction: res.my_reaction };
      if (type === "thread") return { ...prev, thread: { ...prev.thread, ...patch } };
      return { ...prev, replies: prev.replies.map((r) => (r.reply_id === targetId ? { ...r, ...patch } : r)) };
    });
  };

  // Gate every write on participation entitlement (subscriber). Reading stays open.
  const requireParticipate = (verb) => {
    if (!user) { toast(`Sign in to ${verb}.`); return false; }
    if (!canParticipate) { toast(`Joining the conversation is a member perk — subscribe to ${verb} in the forum.`); return false; }
    return true;
  };

  const react = async (type, targetId, value) => {
    if (!requireParticipate("react")) return;
    setReacting(targetId);
    try {
      const { data: res } = await api.post("/forum/react", { target_type: type, target_id: targetId, value });
      applyCounts(type, targetId, res);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setReacting(null); }
  };

  const openReply = (target) => {
    if (!requireParticipate("reply")) return;
    setReplyText("");
    setReplyImages([]);
    setReplyVideo(null);
    setActiveReply(target); // 'op' or a reply_id
  };

  const submitReply = async () => {
    if (!canParticipate || !replyText.trim()) return;
    setReplyBusy(true);
    try {
      const parent = activeReply && activeReply !== "op" ? activeReply : undefined;
      await api.post(`/forum/threads/${id}/replies`, {
        body: replyText.trim(), parent_reply_id: parent, images: replyImages, video: replyVideo,
      });
      setReplyText(""); setReplyImages([]); setReplyVideo(null); setActiveReply(null);
      await load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setReplyBusy(false); }
  };

  const openReport = (type, targetId) => {
    if (!requireParticipate("report posts")) return;
    setReportTarget({ type, id: targetId });
  };
  const submitReport = async (reason, note) => {
    try {
      const { data: res } = await api.post("/forum/flag", { target_type: reportTarget.type, target_id: reportTarget.id, reason, note });
      toast.success(res.already ? "You've already reported this." : "Reported — a moderator will review it.");
      setReportTarget(null);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const deleteThread = async () => {
    if (!window.confirm("Delete this thread and all its replies?")) return;
    try { await api.delete(`/forum/threads/${id}`); toast.success("Thread deleted."); navigate("/forum"); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const deleteReply = async (rid) => {
    try { await api.delete(`/forum/replies/${rid}`); await load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const thread = data?.thread;
  const replies = data?.replies || [];
  const roots = replies.filter((r) => !r.root_reply_id);
  const childrenOf = (rid) => replies.filter((r) => r.root_reply_id === rid);
  const canDeleteThread = thread && user && (thread.author_id === user.user_id || canMod);
  const canDeleteReply = (r) => user && (r.author_id === user.user_id || canMod);
  const locked = !!thread?.locked;

  const renderReply = (r, nested) => (
    <div key={r.reply_id} id={`reply-${r.reply_id}`}>
      <ForumPost
        P={P} post={r} targetType="reply" targetId={r.reply_id} nested={nested}
        canDelete={canDeleteReply(r)} onDelete={() => deleteReply(r.reply_id)}
        onReact={(v) => react("reply", r.reply_id, v)} reacting={reacting === r.reply_id}
        onReport={() => openReport("reply", r.reply_id)}
        onReply={() => (locked ? toast("This thread is locked.") : openReply(r.reply_id))} currentUser={user}
      />
      {activeReply === r.reply_id && !locked && (
        <div className="mt-2 sm:pl-8">
          <ReplyBox P={P} value={replyText} onChange={setReplyText} onSubmit={submitReply} onCancel={() => setActiveReply(null)}
            busy={replyBusy} disabled={!canParticipate} placeholder={`Reply to @${r.author_username}…`} testid={`forum-replybox-${r.reply_id}`}
            images={replyImages} setImages={setReplyImages} video={replyVideo} setVideo={setReplyVideo} />
        </div>
      )}
    </div>
  );

  const bottomReply = () => {
    if (locked) return (
      <div className="mt-4 inline-flex items-center gap-2 font-mono uppercase text-xs px-4 py-2.5 border" style={{ color: P.textMuted, borderColor: P.cardBorder }} data-testid="forum-locked-note">
        <Lock className="w-4 h-4" /> This thread is locked
      </div>
    );
    if (!user) return (
      <Link to="/join" data-testid="forum-open-reply" className="mt-4 inline-flex items-center gap-2 font-mono uppercase text-xs font-bold tracking-wide px-4 py-2.5 bg-olive hover:bg-olive-bright text-white transition-colors">Sign in to reply</Link>
    );
    if (!canParticipate) return (
      <Link to="/pricing" data-testid="forum-subscribe-cta" className="mt-4 inline-flex items-center gap-2 font-mono uppercase text-xs font-bold tracking-wide px-4 py-2.5 bg-olive hover:bg-olive-bright text-white transition-colors">Subscribe to reply</Link>
    );
    return (
      <button onClick={() => openReply("op")} data-testid="forum-open-reply"
        className="mt-4 inline-flex items-center gap-2 font-mono uppercase text-xs font-bold tracking-wide px-4 py-2.5 bg-olive hover:bg-olive-bright text-white transition-colors">Write a reply</button>
    );
  };

  return (
    <div className="min-h-screen" style={{ color: P.text }} data-testid="forum-thread-page">
      <Navbar />
      <div className="relative">
        <div aria-hidden data-testid="forum-bg" className="pointer-events-none absolute inset-0 z-0" style={forumBg} />
        <div className="relative z-[1] max-w-[900px] mx-auto px-4 sm:px-6 py-8 sm:py-12 min-h-[80vh]">
          <div className="flex items-center justify-between gap-3 mb-6">
            <Link to="/forum" data-testid="forum-back" className="inline-flex items-center gap-1.5 font-mono uppercase text-[11px] tracking-wide" style={{ color: P.textMuted }}>
              <ArrowLeft className="w-3.5 h-3.5" /> Back to forum
            </Link>
            <div className="flex items-center gap-2">
            {thread && (
              <ShareLinkButton sharePath={`forum/thread/${thread.thread_id}`} title={thread.title} testid="forum-thread-share-btn"
                className="inline-flex items-center gap-1.5 font-mono uppercase text-[11px] tracking-wide px-3 py-1.5 border transition-colors hover:bg-white/5"
                style={{ color: P.text, borderColor: P.cardBorder }} />
            )}
            {thread && user && (
              <button onClick={toggleWatch} disabled={subBusy} data-testid="forum-watch-toggle"
                title={subscribed ? "You're watching this thread — click to stop" : "Watch this thread to get emailed on new replies"}
                className="inline-flex items-center gap-1.5 font-mono uppercase text-[11px] tracking-wide px-3 py-1.5 border transition-colors disabled:opacity-50"
                style={{ color: subscribed ? "#0b0c07" : P.text, background: subscribed ? P.lime : "transparent", borderColor: subscribed ? P.lime : P.cardBorder }}>
                {subBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (subscribed ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />)}
                {subscribed ? "Watching" : "Watch"}
              </button>
            )}
            </div>
          </div>

          {loading ? (
            <div className="py-16 text-center" style={{ color: P.textMuted }}><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : !thread ? (
            <div className="py-16 text-center border font-mono text-sm" style={{ color: P.textMuted, borderColor: P.cardBorder, background: P.rowSurface }} data-testid="forum-thread-missing">
              This thread no longer exists.
            </div>
          ) : (
            <>
              {/* Inline moderator toolbar (mods/admins only) */}
              {canMod && (
                <ModToolbar P={P} thread={thread} categories={categories} onChanged={load} onRestrict={() => setRestrictOpen(true)} />
              )}

              {/* Original post */}
              <ForumPost
                P={P} post={thread} targetType="thread" targetId={thread.thread_id} isOP
                canDelete={canDeleteThread} onDelete={deleteThread}
                onReact={(v) => react("thread", thread.thread_id, v)} reacting={reacting === thread.thread_id}
                onReport={() => openReport("thread", thread.thread_id)}
                onReply={() => (locked ? toast("This thread is locked.") : openReply("op"))} currentUser={user}
              />
              {activeReply === "op" && !locked && (
                <div className="mt-2">
                  <ReplyBox P={P} value={replyText} onChange={setReplyText} onSubmit={submitReply} onCancel={() => setActiveReply(null)}
                    busy={replyBusy} disabled={!canParticipate} placeholder="Reply to this thread…" testid="forum-replybox-op"
                    images={replyImages} setImages={setReplyImages} video={replyVideo} setVideo={setReplyVideo} />
                </div>
              )}

              {/* Replies */}
              <div className="mt-6">
                <h2 className="font-mono uppercase text-[11px] tracking-[1.5px] mb-3" style={{ color: P.textMuted }} data-testid="forum-replies-count">
                  {replies.length} {replies.length === 1 ? "reply" : "replies"}
                </h2>
                <div className="flex flex-col gap-3">
                  {roots.map((root) => (
                    <div key={root.reply_id} className="flex flex-col gap-2">
                      {renderReply(root, false)}
                      {childrenOf(root.reply_id).length > 0 && (
                        <div className="sm:pl-8 sm:border-l flex flex-col gap-2" style={{ borderColor: P.cardBorder }}>
                          {childrenOf(root.reply_id).map((c) => renderReply(c, true))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {activeReply === null && bottomReply()}
              </div>
            </>
          )}
        </div>
      </div>

      {reportTarget && <ReportModal P={P} onClose={() => setReportTarget(null)} onSubmit={submitReport} />}
      {restrictOpen && thread && (
        <RestrictDialog P={P} authorUsername={thread.author_username} authorId={thread.author_id}
          onClose={() => setRestrictOpen(false)} onDone={load} />
      )}
    </div>
  );
}
